import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { ModalProps } from '../../../../../components/modal/modal';
import { NestedIdValue } from '../../../../../features/multi-select/use-multi-select';
import { SchedulerInterface } from '../../../../../features/scheduler/types';
import { getValueFromOptions } from '../../../../../helpers/mappers';
import { validate } from '../../../../../helpers/validate';
import { useLang } from '../../../../../hooks/use-lang';
import { PostScheduleRequest, SchedulerType } from '../../../../../store/api/scheduler-api';
import { useBranchesStore } from '../../../../../store/use-branch-store';
import { useEmployeesStore } from '../../../../../store/use-employee-store';
import { useSchedulerStore } from '../../../../../store/use-scheduler-store';
import { IdValue } from '../../../../../types/props';

export const useTasksCreatePlannerModal = (props: ModalProps & { scheduler?: SchedulerType }) => {
  const { t, getLocale } = useLang();
  const { postScheduler, isLoading, putScheduler } = useSchedulerStore();
  const { staff } = useEmployeesStore();
  const { place } = useBranchesStore();

  const [task, setTask] = useState<Partial<PostScheduleRequest>>(
    props.scheduler || { is_regular: true, days_of_week: Array.from({ length: 7 }, () => false) },
  );

  const handleCreate = async () => {
    try {
      const dayRules: Record<string, { rule: () => boolean }> = {};
      const rule = () => !!task.days_of_week?.some((item) => !!item);
      task.days_of_week?.forEach((_, index) => {
        dayRules[`day-of-week-${index}`] = {
          rule,
        };
      });

      const specRules: Record<string, { rule: () => boolean }> = {};
      task.start_date?.forEach((spec, index) => {
        specRules[`spec-date-${index}`] = {
          rule: () => !!spec && dayjs(spec).valueOf() >= dayjs().startOf('day').valueOf(),
        };
      });

      const dayValidate = task.is_regular ? dayRules : specRules;
      const isValidSchedule = validate(dayValidate);

      const isValid = validate({
        name: { rule: () => !!task?.title && task.title.length > 0 },
        executor: { rule: () => !!task.staff_ids?.length || !!task.staff_id },
        start_time: { rule: () => !!task.start_time },
      });

      if (!isValid || !isValidSchedule) return;

      const taskWithIds = { ...task, staff_ids: task.staff_ids || [task.staff_id!] };
      props.scheduler ? await putScheduler(taskWithIds) : await postScheduler(taskWithIds);

      props.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const { availableStaff, availableStaffIdValue, branches, selectedBranch } = useMemo(() => {
    const branches =
      place?.map((item) => ({ id: String(item.id), value: getLocale(item.name) })) || [];
    const selectedBranch = branches.find((item) => item.id === String(task.place_id));

    const availableStaff =
      staff?.filter((item) =>
        item.places?.some((place) => String(place.id) === selectedBranch?.id),
      ) || [];

    const availableStaffIdValue: IdValue[] =
      availableStaff?.map((item) => ({
        id: String(item.id),
        value: getLocale(item.name) + ' ' + getLocale(item.surname),
      })) || [];

    return { selectedBranch, availableStaff, availableStaffIdValue, branches };
  }, [place, task.place_id, staff]);

  const { executors, groupedStaffsByTags } = useMemo(() => {
    const groupedStaffsByTags: NestedIdValue[] = [];
    availableStaff?.forEach((item) => {
      const staff: IdValue = {
        id: String(item.id),
        value: getLocale(item.name) + ' ' + getLocale(item.surname),
      };
      item.tags?.forEach((tag) => {
        const tagId = String(tag.id);
        const tagIndex = groupedStaffsByTags.findIndex((resultItem) => resultItem.id === tagId);
        if (tagIndex === -1) {
          groupedStaffsByTags.push({
            id: tagId,
            value: tag.value,
            children: [staff],
          });
        } else {
          groupedStaffsByTags[tagIndex].children!.push(staff);
        }
      });
    });

    const executors = getValueFromOptions(
      availableStaffIdValue,
      task.staff_ids || [task.staff_id || -1],
    );

    return { branches, selectedBranch, executors, groupedStaffsByTags };
  }, [availableStaff, availableStaffIdValue, task.staff_ids]);

  const handleUpdateSpecs: SchedulerInterface['handleUpdateSpecs'] = (e) => {
    const updated = { ...task, start_date: e, is_regular: false };
    setTask(updated);
  };

  const handleUpdateWeek: SchedulerInterface['handleUpdateWeek'] = (e) => {
    const updated = { ...task, days_of_week: e, is_regular: true };
    setTask(updated);
  };

  return {
    branches,
    t,
    task,
    selectedBranch,
    isLoading,
    handleCreate,
    setTask,
    groupedStaffsByTags,
    executors,
    handleUpdateSpecs,
    handleUpdateWeek,
  };
};
